import type { TakeoffLineItem } from '../../hooks/measurements/useTakeoffItems';

// Roofing material types shown in the Material Takeoff dropdown.
// Keys must stay in sync with backend takeoff_defaults.MATERIAL_TYPE_KEYS.
export const MATERIAL_TYPES = [
  { value: 'architectural', label: 'Architectural Shingles' },
  { value: 'three_tab', label: '3-Tab Shingles' },
  { value: 'premium', label: 'Premium Designer Shingles' },
  { value: 'standing_seam', label: 'Standing Seam Metal' },
  { value: 'corrugated', label: 'Corrugated Metal Panels' },
  { value: 'concrete_tile', label: 'Concrete Tile' },
  { value: 'clay_tile', label: 'Clay Tile' },
] as const;

export const CATEGORY_LABELS: Record<TakeoffLineItem['category'], string> = {
  field_materials: 'Field Materials',
  accessories: 'Accessories',
  fasteners_misc: 'Fasteners & Misc',
};

export const CATEGORY_ORDER: TakeoffLineItem['category'][] = [
  'field_materials',
  'accessories',
  'fasteners_misc',
];

export const DRIVER_LABELS: Record<TakeoffLineItem['driver'], string> = {
  squares: 'Squares (true area ÷ 100)',
  true_area_sqft: 'True area (sq ft)',
  ridge_lf: 'Ridge length (lf)',
  hip_lf: 'Hip length (lf)',
  valley_lf: 'Valley length (lf)',
  eave_lf: 'Eave length (lf)',
  rake_lf: 'Rake length (lf)',
  eave_plus_rake_lf: 'Eave + rake perimeter (lf)',
  valleys_plus_eaves_lf: 'Valleys + eaves (lf)',
  fixed: 'Fixed quantity',
};

// Short driver wording used when auto-generating the Notes column
export const DRIVER_SHORT_LABELS: Record<TakeoffLineItem['driver'], string> = {
  squares: 'sq',
  true_area_sqft: 'sf',
  ridge_lf: 'lf ridge',
  hip_lf: 'lf hip',
  valley_lf: 'lf valley',
  eave_lf: 'lf eave',
  rake_lf: 'lf rake',
  eave_plus_rake_lf: 'lf eave + rake',
  valleys_plus_eaves_lf: 'lf valleys + eaves',
  fixed: '',
};

// Measurement figures a catalog item's quantity can be driven by.
// Slope-adjusted the same way the takeoff tab always has: hips, valleys and
// rakes are multiplied by the pitch factor; ridges and eaves are not.
export interface TakeoffDrivers {
  squares: number; // true area ÷ 100, WITHOUT waste (waste applied per item)
  true_area_sqft: number;
  ridge_lf: number;
  hip_lf: number;
  valley_lf: number;
  eave_lf: number;
  rake_lf: number;
  eave_plus_rake_lf: number;
  valleys_plus_eaves_lf: number;
  fixed: number; // always 1
}

export function computeTakeoffQuantity(
  item: Pick<TakeoffLineItem, 'driver' | 'multiplier' | 'coverage_per_unit' | 'include_waste' | 'min_quantity'>,
  drivers: TakeoffDrivers,
  wasteFraction: number,
): number {
  const driverValue = drivers[item.driver] ?? 0;
  const multiplier = Number(item.multiplier) || 0;
  const coverage = Number(item.coverage_per_unit) || 1;
  const withWaste = item.include_waste ? driverValue * (1 + wasteFraction) : driverValue;
  const raw = (withWaste * multiplier) / coverage;
  return Math.max(item.min_quantity, Math.ceil(raw - 1e-9));
}

// Human-readable formula description for the Notes column,
// e.g. "3 per sq • includes 15% waste" or "1000 sf/roll".
export function describeTakeoffFormula(
  item: Pick<TakeoffLineItem, 'driver' | 'multiplier' | 'coverage_per_unit' | 'include_waste' | 'unit_label' | 'min_quantity'>,
  drivers: TakeoffDrivers,
  wastePct: number,
): string {
  const parts: string[] = [];
  const multiplier = Number(item.multiplier) || 0;
  const coverage = Number(item.coverage_per_unit) || 1;
  const driverValue = drivers[item.driver] ?? 0;
  const short = DRIVER_SHORT_LABELS[item.driver];

  if (item.driver === 'fixed') {
    parts.push('fixed quantity');
  } else {
    const baseUnit = item.unit_label.replace(/\s*\(.*\)$/, '');
    if (coverage !== 1 && multiplier !== 1) {
      parts.push(`${Math.round(driverValue).toLocaleString()} ${short} × ${multiplier} ÷ ${coverage}/${singular(baseUnit)}`);
    } else if (coverage !== 1) {
      parts.push(`${coverage} ${short} per ${singular(baseUnit)}`);
    } else if (multiplier !== 1) {
      parts.push(`${multiplier} per ${short}`);
    } else {
      parts.push(`from ${Math.round(driverValue).toLocaleString()} ${short}`);
    }
  }
  if (item.include_waste && item.driver !== 'fixed') {
    parts.push(`includes ${wastePct}% waste`);
  }
  if (item.min_quantity > 0) {
    parts.push(`min ${item.min_quantity}`);
  }
  return parts.join(' • ');
}

function singular(unit: string): string {
  return unit.endsWith('s') ? unit.slice(0, -1) : unit;
}
